import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { HelpdeskApi } from 'api/helpdesk/helpdesk-api.js';
import { HelpdeskDb } from 'lib/cosmos/helpdesk/helpdeskDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

type ExtendedHelpdeskItem = {
	avatar: IImage | undefined;
	name: string;
	isPrimary: boolean;
	date: string;
	id: string;
	userId: string;
	message: string;
};

@Component({
    templateUrl: './helpdesk-conversation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HelpdeskConversationComponent extends TypeCoreComponent<HelpdeskDb.IHelpdeskItem> implements OnInit {
	public readonly chatMessages = signal<ExtendedHelpdeskItem[] | undefined>(undefined);
	public readonly message = signal<string>('');

	public saveHandler = computed<FormSaveHandler>(() => {
		const message = this.message();
		const sharedData = this.sharedData();
		if (!message || !sharedData) {
			return undefined;
		}

		return this.dependencies.httpService
			.post<HelpdeskDb.IHelpdeskItem, HelpdeskApi.ISendMessageRequest>((builder) =>
				builder
					.withCustomApiRoute('helpdesk', 'sendMessage')
					.withData({
						message: message,
						userId: sharedData.data.userId
					})
					.build()
			)
			.pipe(
				map((response) => {
					super.notify(`Message has been sent`);

					this.chatMessages.update((oldValue) => {
						oldValue?.unshift({
							avatar: this.authUser()?.avatar,
							name: 'You',
							message: message,
							id: response.data.messages?.[0]?.id ?? '',
							userId: sharedData.data.userId,
							isPrimary: true,
							date: this.dependencies.timeService.fromNow(response.data.system.created)
						});

						return [...(oldValue ?? [])];
					});

					this.message.set('');
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.initChatMessages(sharedData.data);
		};
	}

	private initChatMessages(data: HelpdeskDb.IHelpdeskItem): void {
		this.chatMessages.set(
			data.messages.map((m) => {
				const isPrimary: boolean = m.senderId === this.authUser()?.id ? true : false;
				const sender = super.getLinkedItemById<UserDb.IUserItem>(m.senderId);

				const extendedMessage: ExtendedHelpdeskItem = {
					userId: data.userId,
					id: data.id,
					message: m.message,
					date: this.dependencies.timeService.fromNow(m.created),
					isPrimary: isPrimary,
					avatar: isPrimary ? this.authUser()?.avatar : sender?.avatar,
					name: isPrimary ? 'You' : nameHelper.getUserName(sender?.email, sender?.firstName, sender?.lastName)
				};

				return extendedMessage;
			})
		);
	}
}
